// **React Imports
import { memo, useCallback, useMemo, useState } from 'react'

// **SCSS Imports
import './index.scss'

// **MUI Imports
import { Box, Button, Tooltip, Typography } from '@mui/material'

// **Component Imports
import { default as Chip, default as CustomChip } from '../chip'
import Icon from '../icon'
import ComprehensionQuestionRenderer from './ComprehensionQuestionRenderer'

// **Third party Imports
import { toast } from 'react-toastify'

// **API Imports
import { addAIQuestionToLibrary, publishQuestion } from '../../axios/api/challenge'

// **Utils Imports
import {
  DIFFICULTY_COLORS_LABEL_OBJ,
  QUESTION_CONTENT_TYPE_MAP,
  QUESTION_SUB_TYPE_LABEL,
  QUESTION_SUB_TYPE_MAP,
  ROLES
} from '../../utils/constant'
import {
  addToLibraryConfirmation,
  checkCurrentRole,
  checkDeveloperAccess,
  checkUserAccess,
  publishConfirmation,
  showErrorToast
} from '../../utils/utils'
import OverlayLoading from '../overlay-loading'
import MCQTypeRenderer from './MCQTypeRenderer'

// custom component
const CustomTooltipContent = memo(() => {
  return (
    <Box
      sx={{
        gap: '16px',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px'
      }}
    >
      <Typography variant="body2" sx={{ color: 'var(--common-white)' }}>
        - Option/Options with green background are correct <br />
        - Option/Options with red background are incorrect and selected by
        candidate
        <br />- Short answer responses are evaluated by AI based on relevance
        and accuracy
      </Typography>
    </Box>
  )
})

const MCQQuestionRenderer = ({
  question,
  questionNo,
  needActionButton,
  actionButtonTitle = '',
  actionButtonHandler = () => { },
  usedForEvaluation = false,
  showActionButton = false,
  showQuestionMetaData = false,
  handleMetaDataUpdateClick = null,
  hasAccess = false,
  showStatus = false, // to show question status also along with other meta details,
  publishPermission = false, // to show publish to global library button
  showAddToLibrary = false, // to show add to library button while previwing the AI generated questions
  refreshQuestionList,
  isPsychometric = false,
  renderAdditionalActions = null,
  useForAiChallenge = false
}) => {
  // states
  const [publishLoading, setPublishLoading] = useState(false)

  // variables
  const isDeveloper = useMemo(() => {
    return checkDeveloperAccess()
  }, [])

  const showPublishButton = useMemo(() => {
    return (
      checkCurrentRole(ROLES.ADMIN) &&
      publishPermission &&
      question?.workspaceId
    )
  }, [publishPermission, question?.workspaceId])

  const showAddToLibraryButton = useMemo(() => {
    return showAddToLibrary && question?.canMoveToLibrary && checkUserAccess([ROLES.ADMIN, ROLES.CONTRIBUTOR])
  }, [showAddToLibrary, question])

  // handlers
  const publishMCQQuestion = useCallback(
    async (questionId) => {
      // confirmation
      const confirmed = await publishConfirmation(true)

      if (!confirmed) {
        return
      }

      setPublishLoading(true)
      const response = await publishQuestion(questionId)
      setPublishLoading(false)

      if (response?.isError) {
        showErrorToast(response)
      } else {
        toast.success('Question published successfully')
        refreshQuestionList()
      }
    },
    [refreshQuestionList]
  )

  const addToLibrary = useCallback(async (questionId) => {
    const confirmation = await addToLibraryConfirmation()

    if (!confirmation || !confirmation?.isConfirmed) {
      return
    }

    setPublishLoading(true)
    const response = await addAIQuestionToLibrary(questionId, confirmation?.isLive)
    setPublishLoading(false)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      toast.success('Question added to library successfully')
      await refreshQuestionList()
    }
  }, [refreshQuestionList])

  return (
    <>
      {publishLoading && <OverlayLoading />}
      <Box
        className={`${question?.content === QUESTION_CONTENT_TYPE_MAP?.PLAIN_TEXT
          ? 'text'
          : question?.content === QUESTION_CONTENT_TYPE_MAP.HTML
            ? 'html'
            : 'markdown'
          }-question question${question?.subtype === QUESTION_SUB_TYPE_MAP.COMPREHENSION ? ' question-comprehension' : ''}`}
      >
        <Box className="question-control">
          {question ? (
            <Box className="question-number-heading-container">
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {`Q. ${questionNo}`}
              </Typography>

              {showQuestionMetaData && (
                <Box className="chip-container">
                  {question?.subtype && !isPsychometric && (
                    <CustomChip
                      size="small"
                      rounded
                      label={`${QUESTION_SUB_TYPE_LABEL?.[question?.subtype]}`}
                      color="info"
                    />
                  )}

                  {!isDeveloper &&
                    !question?.isAiChallenge &&
                    !useForAiChallenge && (
                      <Chip
                        size="small"
                        rounded
                        label={
                          question?.workspaceId
                            ? 'Your Library'
                            : 'CoderScout Library'
                        }
                        color={question?.workspaceId ? 'secondary' : 'primary'}
                      />
                    )}
                  {question?.topicName && (
                    <Tooltip
                      title={usedForEvaluation ? '' : question?.topicName}
                      arrow
                    >
                      <Box>
                        <Chip
                          size="small"
                          rounded
                          color="primary"
                          label={
                            usedForEvaluation
                              ? question?.topicName || ''
                              : 'Skill'
                          }
                          icon={
                            !usedForEvaluation &&
                            question?.topicName && (
                              <Icon
                                icon={'ri:target-fill'}
                                style={{ cursor: 'default', fontSize: '16px' }}
                              />
                            )
                          }
                        />
                      </Box>
                    </Tooltip>
                  )}

                  {!usedForEvaluation && !isPsychometric && (
                    <CustomChip
                      size="small"
                      rounded
                      label={`${question?.mark} marks`}
                    />
                  )}
                  {question?.maxCharacters && (
                    <CustomChip
                      size="small"
                      rounded
                      label={`Max Chars: ${question?.maxCharacters}`}
                    />
                  )}

                  <CustomChip
                    size="small"
                    rounded
                    label={
                      DIFFICULTY_COLORS_LABEL_OBJ?.[question?.difficulty]?.label
                    }
                    color={
                      DIFFICULTY_COLORS_LABEL_OBJ?.[question?.difficulty]?.color
                    }
                  />

                  <Tooltip
                    title={question?.tags?.join(', ')?.toUpperCase()}
                    arrow
                  >
                    <Box>
                      <Chip
                        size="small"
                        rounded
                        icon={
                          <Icon
                            icon={'mdi:tags'}
                            style={{ cursor: 'default' }}
                          />
                        }
                        label={`Tags ${question?.tags?.length}`}
                      />
                    </Box>
                  </Tooltip>

                  {(isDeveloper || showStatus) && (
                    <Chip
                      rounded
                      size="small"
                      color={question?.isLive ? 'success' : 'warning'}
                      label={question?.isLive ? 'Published' : 'Draft'}
                    />
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Typography>No Questions to preview</Typography>
          )}

          <Box className="action-button-container">
            {((question && showActionButton) || question?.workspaceId) &&
              hasAccess &&
              handleMetaDataUpdateClick && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    handleMetaDataUpdateClick(question)
                  }}
                >
                  Edit Metadata
                </Button>
              )}
            {renderAdditionalActions &&
              hasAccess &&
              renderAdditionalActions(question)}
            {((question && showActionButton) ||
              (needActionButton && question && question?.workspaceId)) &&
              hasAccess && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    actionButtonHandler(question)
                  }}
                >
                  {actionButtonTitle}
                </Button>
              )}

            {showPublishButton && (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  publishMCQQuestion(question?.id)
                }}
                startIcon={
                  <Icon icon={'entypo:publish'} style={{ fontSize: '15px' }} />
                }
              >
                Move to Global Library
              </Button>
            )}
            {showAddToLibraryButton && (
              <Button
                variant="outlined"
                size="small"
                color="primary"
                onClick={() => {
                  addToLibrary(question?.id)
                }}
                startIcon={
                  <Icon
                    icon={'entypo:publish'}
                    style={{ fontSize: '15px' }}
                  />
                }
              >
                Add to Library
              </Button>
            )}
          </Box>

          {usedForEvaluation && !isPsychometric && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {question?.score != null &&
                question?.subtype !== QUESTION_SUB_TYPE_MAP.COMPREHENSION && (
                  <CustomChip
                    rounded
                    size="small"
                    label={`${question?.score} / ${question.mark} marks`}
                    color={
                      question?.score === 0
                        ? 'error'
                        : question?.score === question?.mark
                          ? 'success'
                          : 'warning'
                    }
                  />
                )}
              <Icon
                title={<CustomTooltipContent />}
                icon={'material-symbols:info-outline'}
                className="icon"
                style={{ cursor: 'default', color: 'var(--text-secondary)' }}
              />
            </Box>
          )}
        </Box>

        {/* Comprehension layout */}
        {question?.subtype === QUESTION_SUB_TYPE_MAP.COMPREHENSION ? (
          <ComprehensionQuestionRenderer
            question={question}
            questionNo={questionNo}
            usedForEvaluation={usedForEvaluation}
          />
        ) : (
          <MCQTypeRenderer
            question={question}
            usedForEvaluation={usedForEvaluation}
            isPsychometric={isPsychometric}
          />
        )}
      </Box>
    </>
  )
}
export default memo(MCQQuestionRenderer)
